import type { ChangeEvent } from 'react';
import { UserRound } from 'lucide-react';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { LiksFormFieldLabel } from '../../components/liks/LiksFormFieldLabel/LiksFormFieldLabel';
import { LiksPhotoUpload } from '../../components/liks/LiksPhotoUpload/LiksPhotoUpload';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../i18n/useI18n';
import type { LacClientRole } from './lacRoles';

type RoleOption = {
  value: string;
  label: string;
};

type LacUserProfileFormFieldsProps = {
  tenantDisplayName: string;
  roleFieldId: string;
  role: LacClientRole;
  roleOptions: RoleOption[];
  onRoleChange: (value: string) => void;
  isRoleLoading: boolean;
  isRoleLoadError: boolean;
  email: string;
  onEmailChange: (value: string) => void;
  isEmailInvalid: boolean;
  username: string;
  firstName: string;
  onFirstNameChange: (value: string) => void;
  lastName: string;
  onLastNameChange: (value: string) => void;
  validFrom: string;
  onValidFromChange: (value: string) => void;
  validTo: string;
  onValidToChange: (value: string) => void;
  isValidToBeforeValidFrom: boolean;
  photoFieldId: string;
  photoDisplaySrc: string | null;
  photoError: string | null;
  isPhotoBusy: boolean;
  isBusy: boolean;
  isSubmitting: boolean;
  onPhotoChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemovePhoto: () => void;
};

export function LacUserProfileFormFields({
  tenantDisplayName,
  roleFieldId,
  role,
  roleOptions,
  onRoleChange,
  isRoleLoading,
  isRoleLoadError,
  email,
  onEmailChange,
  isEmailInvalid,
  username,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  validFrom,
  onValidFromChange,
  validTo,
  onValidToChange,
  isValidToBeforeValidFrom,
  photoFieldId,
  photoDisplaySrc,
  photoError,
  isPhotoBusy,
  isBusy,
  isSubmitting,
  onPhotoChange,
  onRemovePhoto,
}: LacUserProfileFormFieldsProps) {
  const { translate } = useI18n();

  return (
    <div className="lac-user-profile__body">
      <div className="lac-user-profile__fields">
        <div className="lac-create-user__row">
          <LiksTextFormField
            label={translate('lac.users.create.organisationLabel')}
            required
            value={tenantDisplayName}
            disabled
            readOnly
          />

          <div className="liks-text-form-field">
            <LiksFormFieldLabel htmlFor={roleFieldId} required>
              {translate('lac.users.roleLabel')}
            </LiksFormFieldLabel>
            {isRoleLoading ? (
              <p className="lac-create-user__hint">
                {translate('lac.users.profile.roleLoading')}
              </p>
            ) : (
              <>
                <LiksDropdown
                  id={roleFieldId}
                  className="lac-create-user__dropdown"
                  variant="on-light"
                  value={role}
                  onChange={onRoleChange}
                  options={roleOptions}
                  aria-label={translate('lac.users.roleLabel')}
                  disabled={isSubmitting}
                />
                {isRoleLoadError && (
                  <p className="lac-create-user__hint" role="alert">
                    {translate('lac.users.profile.roleLoadError')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="lac-create-user__row">
          <LiksTextFormField
            label={translate('lac.users.create.emailLabel')}
            required
            type="email"
            value={email}
            onChange={onEmailChange}
            disabled={isSubmitting}
            autoComplete="off"
            error={
              isEmailInvalid ? translate('lac.users.create.emailInvalid') : null
            }
          />

          <LiksTextFormField
            label={translate('lac.users.create.usernameLabel')}
            required
            value={username}
            disabled
            readOnly
            autoComplete="off"
          />
        </div>

        <div className="lac-create-user__row">
          <LiksTextFormField
            label={translate('lac.users.create.firstNameLabel')}
            required
            value={firstName}
            onChange={onFirstNameChange}
            disabled={isSubmitting}
            autoComplete="off"
          />

          <LiksTextFormField
            label={translate('lac.users.create.lastNameLabel')}
            required
            value={lastName}
            onChange={onLastNameChange}
            disabled={isSubmitting}
            autoComplete="off"
          />
        </div>

        <div className="lac-create-user__row">
          <LiksTextFormField
            label={translate('lac.users.create.validFromLabel')}
            type="date"
            value={validFrom}
            onChange={onValidFromChange}
            disabled={isSubmitting}
          />

          <LiksTextFormField
            label={translate('lac.users.create.validToLabel')}
            type="date"
            value={validTo}
            onChange={onValidToChange}
            disabled={isSubmitting}
            error={
              isValidToBeforeValidFrom
                ? translate('lac.users.create.validToBeforeValidFrom')
                : null
            }
          />
        </div>
      </div>

      <LiksPhotoUpload
        className="lac-user-profile__photo"
        src={photoDisplaySrc}
        isLoading={isPhotoBusy}
        placeholder={<UserRound strokeWidth={1.5} aria-hidden />}
        inputId={photoFieldId}
        disabled={isBusy}
        onFileChange={(event) => {
          void onPhotoChange(event);
        }}
        onRemove={onRemovePhoto}
        error={photoError}
      />
    </div>
  );
}
